import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { axiosAuth } from "../api/axiosConfig";

const UserContext = createContext();

export function UserProvider(props) {
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  //Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        const res = await axiosAuth.get("/user/me");
        if (res.data) {
          setUser({
            ...res.data,
            isAuthenticated: true,
          });
          const settingsRes = await axiosAuth.get("/user/get-settings");
          setSettings(settingsRes.data);
        } else {
          setUser({ isAuthenticated: false });
          setSettings({});
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        // Only treat this as "not authenticated" when the server actually said
        // so (401/403). A network error, timeout, or 5xx just means we
        // couldn't confirm the session's status right now — it doesn't mean
        // the session is invalid, so don't force a logout side effect for
        // those transient cases.
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setUser({ isAuthenticated: false });
          setSettings({});
          try {
            await logout();
          } catch (e) {
            // ignore logout errors here
          }
        }
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await axiosAuth.post("/user/login", { email, password });
      const userData = res.data;

      if (userData) {
        //update user state
        setUser({ ...userData.user, isAuthenticated: true });
        // Use settings from login response to avoid race condition
        if (userData.settings) {
          setSettings(userData.settings);
        }
        setLoading(false);
        return true;
      }
      throw new Error("Login failed: No token received");
    } catch (error) {
      console.error("Login failed:", error);
      setUser({ isAuthenticated: false });
      setSettings({});
      setLoading(false);
      // Rethrown as-is so the login page can show the message and the
      // request reference separately (api/apiError.js).
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axiosAuth.post("/user/logout");
    } catch (error) {
      // The server-side logout call can fail transiently (e.g. network not yet
      // reconnected after the machine wakes from sleep). Don't let that leave
      // the client silently believing the user is still logged in — clear
      // local session state regardless, since the whole point of calling
      // logout() is that the session is already meant to be over.
      console.error("Server-side logout failed; clearing local session anyway:", error);
    } finally {
      setUser({ isAuthenticated: false });
      setSettings({});
      setLoading(false);
    }
    //Make sure to put the redirect to the component that calls this function
    // history.push("/login");
  }, []);

  const updateSettings = useCallback(async () => {
    try {
      const settingsRes = await axiosAuth.get("/user/get-settings");
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to update settings:", error);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      loading,
      settings,
      updateSettings,
    }),
    [user, login, logout, settings, updateSettings]
  );

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
}

export const useUserContext = () => useContext(UserContext);
